import { useEffect, useState } from 'react';
import { Group, Panel, Separator } from 'react-resizable-panels';
import Editor, { DiffEditor } from '@monaco-editor/react';
import { ROTAS } from '@remoto/shared';
import { chamar, ErroApi } from '../api';
import { usarSessao } from '../estado/sessao';
import { BotaoLeve } from '../componentes/layout';
import { IconeFechar, IconePonto } from '../componentes/icones';
import { PreviewHtml, PreviewMarkdown, PreviewMidia, especieDe } from '../componentes/preview';
import { linguagemDe } from '../editor/monaco';
import { usarTemaDoEditor } from '../estado/tema';
import {
  abaAtivaDe,
  abasDe,
  fecharAbaEm,
  moverAba,
  nomeDaAba,
  rotulosDasAbas,
  type EstadoDoWorkspace,
  type Metade,
  TIPO_ARRASTO,
} from './estado';
import { esquecerRascunho, guardarRascunho, recuperarRascunho } from './memoria';
import { mensagemDoErro } from '../erros';

interface Aberto {
  caminho: string;
  conteudo: string;
  hash: string;
  /** Veio de raiz de espiada, ou de raiz `ro`: dá para ler, nunca para salvar. */
  somenteLeitura?: boolean;
}

/**
 * O caminho do modelo do Monaco — obrigatório, não decorativo.
 *
 * Sem `path`, `@monaco-editor/react` cria todos os modelos na mesma URI vazia.
 * O efeito é feio e silencioso: os dois lados de uma comparação viram o mesmo
 * texto (nenhuma diferença aparece), o cursor vaza de um arquivo para o outro e
 * um modelo já existente ignora o conteúdo novo. A máquina entra na chave
 * porque o mesmo caminho existe no PC e na VPS e são arquivos diferentes.
 */
function modeloDe(papel: string, maquinaId: string, caminho: string): string {
  return `${papel}/${maquinaId}/${caminho}`;
}

/**
 * Um grupo de abas com o conteúdo embaixo.
 *
 * Arquivo de texto abre no Monaco; Markdown, PDF, imagem, áudio e vídeo abrem
 * no preview que já existia. Cada grupo é uma instância própria — é o que faz
 * a área dividida funcionar sem inventar nada: dois grupos são dois editores
 * com o próprio rascunho, o próprio conflito e o próprio Ctrl+K.
 */
function GrupoDeAbas({
  maquinaId,
  metade,
  abas,
  aba,
  ativo,
  aoEscolherAba,
  aoFecharAba,
  aoSoltarAqui,
}: {
  maquinaId: string;
  metade: Metade;
  abas: string[];
  aba: string;
  /**
   * Se este é o grupo em foco.
   *
   * Com a área dividida existem dois editores na tela, e Ctrl+S é um atalho de
   * janela: sem isto, salvar um arquivo salvaria os dois.
   */
  ativo: boolean;
  aoEscolherAba: (caminho: string) => void;
  aoFecharAba: (caminho: string) => void;
  /** Um arquivo largado nas abas deste grupo vem para cá. */
  aoSoltarAqui: (caminho: string) => void;
}): React.JSX.Element {
  const papel = usarSessao((e) => e.usuario?.papel);
  const temaDoEditor = usarTemaDoEditor();
  const papelPodeSalvar = papel === 'ADMIN' || papel === 'OPERADOR';

  const [aberto, setAberto] = useState<Aberto | null>(null);
  const [rascunho, setRascunho] = useState('');
  const [erro, setErro] = useState('');
  // Sem este estado, o `catch` gravava o erro e o JSX caía no ramo final:
  // a tela mostrava a falha E "abrindo…" logo abaixo, para sempre.
  const [abrindo, setAbrindo] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [verRenderizado, setVerRenderizado] = useState(true);
  const [conflito, setConflito] = useState<{ meu: string; deles: string; hash: string } | null>(
    null,
  );
  const [instrucao, setInstrucao] = useState('');
  const [proposta, setProposta] = useState<string | null>(null);
  const [pensando, setPensando] = useState(false);

  // Papel manda, mas o arquivo tem a última palavra: espiar fora da raiz é
  // leitura pura, e oferecer Salvar ali seria prometer o que a máquina recusa.
  const podeSalvar = papelPodeSalvar && aberto?.somenteLeitura !== true;

  const especie = aba ? especieDe(aba) : null;
  // Markdown e HTML passam pelo editor como texto: são arquivos que dá para
  // ler cru e para salvar. O que os separa da mídia é isto, não o preview.
  const ehTexto = especie === 'markdown' || especie === 'html';
  const ehMidia = especie !== null && !ehTexto;
  const ehMarkdown = especie === 'markdown';
  const ehHtml = especie === 'html';
  const sujo = aberto !== null && rascunho !== aberto.conteudo;
  const rotulos = rotulosDasAbas(abas);

  // Mídia não passa pelo editor: `fs.ler` recusa binário, e com razão.
  useEffect(() => {
    if (!aba || !maquinaId || ehMidia) {
      setAberto(null);
      return;
    }
    let vivo = true;
    setErro('');
    setConflito(null);
    setProposta(null);
    setAbrindo(true);
    void chamar<Aberto>(`${ROTAS.maquinas}/${maquinaId}/arquivo?caminho=${encodeURIComponent(aba)}`)
      .then((arquivo) => {
        if (!vivo) return;
        setAberto(arquivo);
        // O rascunho só volta se o arquivo no disco continuar o mesmo: por
        // cima de uma alteração da sessão, restaurar seria desfazer o trabalho
        // dela sem avisar.
        setRascunho(recuperarRascunho(maquinaId, arquivo.caminho, arquivo.hash) ?? arquivo.conteudo);
        const tipo = especieDe(arquivo.caminho);
        setVerRenderizado(tipo === 'markdown' || tipo === 'html');
      })
      .catch((e) => {
        if (vivo) setErro(mensagemDoErro(e, 'Não foi possível abrir este arquivo.'));
      })
      .finally(() => {
        if (vivo) setAbrindo(false);
      });
    return () => {
      vivo = false;
    };
  }, [aba, maquinaId, ehMidia]);

  async function salvar(): Promise<void> {
    if (!aberto) return;
    setSalvando(true);
    setErro('');
    try {
      const salvo = await chamar<{ hash: string }>(`${ROTAS.maquinas}/${maquinaId}/arquivo`, {
        method: 'PUT',
        corpo: { caminho: aberto.caminho, conteudo: rascunho, hashEsperado: aberto.hash },
      });
      setAberto({ ...aberto, conteudo: rascunho, hash: salvo.hash });
      // Salvou: o rascunho virou o arquivo, e guardá-lo passaria a competir
      // com o disco na próxima abertura.
      esquecerRascunho(maquinaId, aberto.caminho);
      setConflito(null);
    } catch (e) {
      // Conflito não é erro: é o arquivo tendo mudado no disco. A tela mostra
      // os dois lados para a pessoa escolher, em vez de sobrescrever calado.
      if (e instanceof ErroApi && e.codigo === 'conflito_de_conteudo') {
        const detalhe = e.detalhes as { conteudoAtual?: string; hashAtual?: string } | undefined;
        // Sem os dois lados não há comparação para mostrar, e "manter o meu"
        // gravaria com hash vazio — ou seja, por cima do arquivo sem conferir
        // nada. Melhor recusar e mandar reabrir.
        if (typeof detalhe?.hashAtual !== 'string' || typeof detalhe.conteudoAtual !== 'string') {
          setErro('O arquivo mudou no disco. Feche a aba e abra de novo para ver a diferença.');
        } else {
          setConflito({ meu: rascunho, deles: detalhe.conteudoAtual, hash: detalhe.hashAtual });
        }
      } else {
        setErro(mensagemDoErro(e, 'Não foi possível salvar.'));
      }
    } finally {
      setSalvando(false);
    }
  }

  /** Ctrl+K: pede a alteração e guarda a proposta. Nada é gravado. */
  async function pedirAlteracao(): Promise<void> {
    if (!aberto || instrucao.trim().length < 3) return;
    setPensando(true);
    setErro('');
    try {
      const resposta = await chamar<{ proposta: string }>(
        `${ROTAS.maquinas}/${maquinaId}/arquivo/propor`,
        {
          method: 'POST',
          corpo: { caminho: aberto.caminho, conteudo: rascunho, instrucao: instrucao.trim() },
        },
      );
      setProposta(resposta.proposta);
    } catch (e) {
      setErro(mensagemDoErro(e, 'Não foi possível pedir a alteração.'));
    } finally {
      setPensando(false);
    }
  }

  // Ctrl+S salva e Ctrl+K abre o pedido; os dois são atalhos do navegador.
  useEffect(() => {
    const aoTeclar = (evento: KeyboardEvent): void => {
      if (!ativo) return;
      if (!(evento.ctrlKey || evento.metaKey)) return;
      const tecla = evento.key.toLowerCase();
      if (tecla === 's' && aberto && podeSalvar) {
        evento.preventDefault();
        void salvar();
      }
      if (tecla === 'k' && aberto) {
        evento.preventDefault();
        document.getElementById('pedido-ctrlk-' + metade)?.focus();
      }
    };
    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  });

  if (abas.length === 0) {
    return (
      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          const caminho = e.dataTransfer.getData(TIPO_ARRASTO);
          if (caminho) aoSoltarAqui(caminho);
        }}
        className="flex h-full items-center justify-center p-6 text-center text-sm text-zinc-600"
      >
        Escolha um arquivo no explorador à esquerda.
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Abas */}
      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          const caminho = e.dataTransfer.getData(TIPO_ARRASTO);
          if (caminho) aoSoltarAqui(caminho);
        }}
        className="flex shrink-0 items-stretch overflow-x-auto border-b border-zinc-800"
      >
        {abas.map((caminho) => (
          <div
            key={caminho}
            draggable
            onDragStart={(e) => {
              e.dataTransfer.setData(TIPO_ARRASTO, caminho);
              e.dataTransfer.effectAllowed = 'move';
            }}
            className={
              'group flex shrink-0 items-center gap-1.5 border-r border-zinc-800 px-3 py-1.5 text-xs transition ' +
              (caminho === aba
                ? 'bg-zinc-900 text-zinc-100'
                : 'text-zinc-500 hover:bg-zinc-900/50 hover:text-zinc-300')
            }
          >
            <button onClick={() => aoEscolherAba(caminho)} title={caminho} className="max-w-[22ch] truncate">
              {rotulos.get(caminho) ?? nomeDaAba(caminho)}
            </button>
            <button
              onClick={() => aoFecharAba(caminho)}
              title={caminho === aba && sujo ? 'Fechar — há mudança não salva' : 'Fechar'}
              aria-label={
                (caminho === aba && sujo ? 'Fechar (não salvo) ' : 'Fechar ') + nomeDaAba(caminho)
              }
              className="flex items-center rounded p-0.5 text-zinc-600 transition hover:bg-zinc-800 hover:text-zinc-200"
            >
              {caminho === aba && sujo ? (
                <IconePonto tamanho={14} className="text-amber-300" />
              ) : (
                <IconeFechar tamanho={14} />
              )}
            </button>
          </div>
        ))}
      </div>

      {/* Barra de ações do arquivo aberto */}
      {aberto ? (
        <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-zinc-800 px-3 py-1.5 text-xs">
          <span className="truncate font-mono text-zinc-600" title={aberto.caminho}>
            {aberto.caminho}
          </span>
          <div className="flex-1" />
          {ehTexto ? (
            <BotaoLeve onClick={() => setVerRenderizado((v) => !v)}>
              {verRenderizado ? 'Editar' : 'Ver formatado'}
            </BotaoLeve>
          ) : null}
          {sujo ? <span className="text-amber-300">não salvo</span> : null}
          {podeSalvar ? (
            <BotaoLeve onClick={() => void salvar()} disabled={!sujo || salvando}>
              {salvando ? 'salvando…' : 'Salvar'}
            </BotaoLeve>
          ) : null}
        </div>
      ) : null}

      {erro ? <p className="shrink-0 px-3 py-2 text-sm text-amber-300">{erro}</p> : null}

      <div className="min-h-0 flex-1 overflow-hidden">
        {ehMidia ? (
          <div className="h-full overflow-auto p-3">
            <PreviewMidia
              maquinaId={maquinaId}
              caminho={aba}
              especie={especie as 'pdf' | 'imagem' | 'audio' | 'video'}
            />
          </div>
        ) : conflito && aberto ? (
          <div className="flex h-full flex-col">
            <p className="shrink-0 px-3 py-2 text-sm text-amber-300">
              O arquivo mudou no disco depois que você abriu. À esquerda o que está lá agora, à
              direita o seu texto.
            </p>
            <div className="min-h-0 flex-1">
              <DiffEditor
                original={conflito.deles}
                modified={conflito.meu}
                originalModelPath={modeloDe('conflito-disco-' + metade, maquinaId, aberto.caminho)}
                modifiedModelPath={modeloDe('conflito-meu-' + metade, maquinaId, aberto.caminho)}
                language={linguagemDe(aberto.caminho)}
                theme={temaDoEditor}
                options={{ readOnly: true, renderSideBySide: true, fontSize: 13 }}
              />
            </div>
            <div className="flex shrink-0 gap-2 p-3">
              <BotaoLeve
                onClick={() => {
                  setAberto({ ...aberto, conteudo: conflito.deles, hash: conflito.hash });
                  setRascunho(conflito.deles);
                  setConflito(null);
                }}
              >
                Ficar com o do disco
              </BotaoLeve>
              <BotaoLeve
                onClick={() => {
                  setAberto({ ...aberto, conteudo: conflito.deles, hash: conflito.hash });
                  setConflito(null);
                }}
              >
                Manter o meu e salvar de novo
              </BotaoLeve>
            </div>
          </div>
        ) : proposta !== null && aberto ? (
          <div className="flex h-full flex-col">
            <p className="shrink-0 px-3 py-2 text-sm text-zinc-400">
              À esquerda o seu arquivo, à direita a proposta. Nada foi gravado.
            </p>
            <div className="min-h-0 flex-1">
              <DiffEditor
                original={rascunho}
                modified={proposta}
                originalModelPath={modeloDe('proposta-atual-' + metade, maquinaId, aberto.caminho)}
                modifiedModelPath={modeloDe('proposta-nova-' + metade, maquinaId, aberto.caminho)}
                language={linguagemDe(aberto.caminho)}
                theme={temaDoEditor}
                options={{ readOnly: true, renderSideBySide: true, fontSize: 13 }}
              />
            </div>
            <div className="flex shrink-0 gap-2 p-3">
              <BotaoLeve
                onClick={() => {
                  setRascunho(proposta);
                  setProposta(null);
                  setInstrucao('');
                }}
              >
                Aceitar (ainda falta salvar)
              </BotaoLeve>
              <BotaoLeve onClick={() => setProposta(null)}>Descartar</BotaoLeve>
            </div>
          </div>
        ) : aberto && ehMarkdown && verRenderizado ? (
          <div className="h-full overflow-auto p-4">
            <PreviewMarkdown texto={rascunho} />
          </div>
        ) : aberto && ehHtml && verRenderizado ? (
          <PreviewHtml texto={rascunho} nome={aberto.caminho} />
        ) : aberto ? (
          <Editor
            value={rascunho}
            path={modeloDe('editor-' + metade, maquinaId, aberto.caminho)}
            language={linguagemDe(aberto.caminho)}
            theme={temaDoEditor}
            onChange={(v) => {
              const texto = v ?? '';
              setRascunho(texto);
              if (aberto) {
                if (texto === aberto.conteudo) esquecerRascunho(maquinaId, aberto.caminho);
                else guardarRascunho(maquinaId, aberto.caminho, texto, aberto.hash);
              }
            }}
            options={{
              readOnly: !podeSalvar,
              fontSize: 13,
              minimap: { enabled: false },
              scrollBeyondLastLine: false,
              automaticLayout: true,
            }}
          />
        ) : abrindo ? (
          <p className="p-3 text-sm text-zinc-500">abrindo…</p>
        ) : null}
      </div>

      {aberto && podeSalvar && proposta === null && !conflito && !verRenderizado ? (
        <form
          className="flex shrink-0 gap-2 border-t border-zinc-800 p-2"
          onSubmit={(evento) => {
            evento.preventDefault();
            void pedirAlteracao();
          }}
        >
          <input
            id={"pedido-ctrlk-" + metade}
            value={instrucao}
            onChange={(e) => setInstrucao(e.target.value)}
            placeholder="Ctrl+K — peça uma alteração neste arquivo…"
            disabled={pensando}
            className="flex-1 rounded-md border border-zinc-700 bg-zinc-950 px-3 py-1.5 text-sm text-zinc-200 placeholder:text-zinc-600"
          />
          <BotaoLeve type="submit" disabled={pensando || instrucao.trim().length < 3}>
            {pensando ? 'pensando…' : 'Pedir'}
          </BotaoLeve>
        </form>
      ) : null}
    </div>
  );
}

/**
 * O centro do workspace: um grupo de abas, ou dois lado a lado.
 *
 * A divisão nasce de arrastar — uma aba, ou um arquivo direto da árvore — para
 * a faixa da direita. É como se divide um editor de verdade, e evita um botão
 * a mais numa barra que já tem bastante coisa.
 *
 * Os dois painéis ficam sempre montados e o da direita encolhe para zero
 * quando não há nada nele: desmontar jogaria fora o rascunho e o estado do
 * editor cada vez que a última aba daquele lado fechasse.
 */
export function Centro({
  maquinaId,
  estado,
  aoMudar,
}: {
  maquinaId: string;
  estado: EstadoDoWorkspace;
  aoMudar: (proximo: EstadoDoWorkspace) => void;
}): React.JSX.Element {
  const [arrastando, setArrastando] = useState(false);
  const [metadeAtiva, setMetadeAtiva] = useState<Metade>('a');
  const dividido = estado.abasB.length > 0;

  /**
   * A metade B não existe enquanto não há o que pôr nela.
   *
   * Antes ela era escondida por `setLayout` (e, antes disso, por `collapse()`).
   * As duas falharam pelo mesmo motivo, que só a medição mostrou: com o
   * StrictMode e a remontagem do `Outlet`, o grupo perde o registro dos
   * painéis — `getLayout()` passa a devolver `{}` — e o comando imperativo
   * fica sem em quê agir, enquanto os painéis na tela ficam com o
   * `defaultSize` de 50/50. Resultado: meia tela de convite "Escolha um
   * arquivo no explorador".
   *
   * Não renderizar o painel é a única forma que não depende de tempo nem de
   * referência viva. Muda o conjunto de painéis, o que a moldura do Shell não
   * pode fazer (lá remontaria a conversa e o socket junto) — aqui pode: sem
   * divisão, a metade B não tem aba nenhuma, e não há estado a perder.
   */

  function grupo(metade: Metade): React.JSX.Element {
    return (
      <div
        className="h-full"
        onMouseDownCapture={() => setMetadeAtiva(metade)}
        onFocusCapture={() => setMetadeAtiva(metade)}
      >
        <GrupoDeAbas
          maquinaId={maquinaId}
          metade={metade}
          ativo={!dividido ? metade === 'a' : metadeAtiva === metade}
          abas={abasDe(estado, metade)}
          aba={abaAtivaDe(estado, metade)}
          aoEscolherAba={(caminho) =>
            aoMudar(metade === 'a' ? { ...estado, aba: caminho } : { ...estado, abaB: caminho })
          }
          aoFecharAba={(caminho) => aoMudar(fecharAbaEm(estado, metade, caminho))}
          aoSoltarAqui={(caminho) => aoMudar(moverAba(estado, metade, caminho))}
        />
      </div>
    );
  }

  return (
    <div
      className="relative flex h-full min-h-0 flex-1"
      onDragOver={(e) => {
        if (e.dataTransfer.types.includes(TIPO_ARRASTO)) setArrastando(true);
      }}
      onDragLeave={(e) => {
        // Só some quando o ponteiro sai da área toda, não ao cruzar um filho.
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setArrastando(false);
      }}
      onDrop={() => setArrastando(false)}
    >
      <Group
        orientation="horizontal"
        className="flex min-w-0 flex-1"
        // Mesma história do Shell: a barra de rolagem do editor encosta na
        // divisória, e sem uma área de arrasto de gente ela ganha o clique.
        resizeTargetMinimumSize={{ coarse: 24, fine: 12 }}
      >
        <Panel
          id="edicao-a"
          defaultSize={dividido ? '50' : '100'}
          minSize="20"
          className="min-w-0 overflow-hidden"
        >
          {grupo('a')}
        </Panel>

        {dividido ? (
          <>
            <Separator className="group relative z-20 w-px shrink-0 cursor-col-resize bg-zinc-800 outline-none">
              <span className="absolute inset-y-0 -left-1.5 -right-1.5 transition group-hover:bg-zinc-500/60" />
            </Separator>
            <Panel id="edicao-b" defaultSize="50" minSize="20" className="min-w-0 overflow-hidden">
              {grupo('b')}
            </Panel>
          </>
        ) : null}
      </Group>

      {/* A faixa que cria a divisão. Só existe enquanto algo está sendo
          arrastado, e só quando ainda não há dois grupos. */}
      {arrastando && !dividido ? (
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            setArrastando(false);
            const caminho = e.dataTransfer.getData(TIPO_ARRASTO);
            if (caminho) aoMudar(moverAba(estado, 'b', caminho));
          }}
          className="absolute inset-y-0 right-0 z-10 flex w-1/3 items-center justify-center border-l-2 border-dashed border-emerald-600 bg-emerald-950/30 text-xs text-emerald-300"
        >
          soltar aqui para dividir
        </div>
      ) : null}
    </div>
  );
}
